import React, { useEffect, useState, useRef } from 'react';
import { isMobile } from './deviceDetection';
type UseInViewOptions = {
  triggerOnce?: boolean;
  rootMargin?: string;
  threshold?: number | number[];
};
/**
 * Custom hook that tracks whether an element is in the viewport
 * Optimized for mobile with reduced sensitivity
 */
export const useInView = <T extends Element,>({
  triggerOnce = false,
  rootMargin = '0px',
  threshold = 0.1
}: UseInViewOptions = {}): {
  ref: RefObject<T>;
  inView: boolean;
} => {
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    // Don't run on server
    if (typeof window === 'undefined') return;
    const element = ref.current;
    if (!element) return;
    // Use more conservative settings on mobile
    const mobileSettings = isMobile() ? {
      rootMargin: '0px',
      threshold: 0.2 // Higher threshold on mobile (more of element must be visible)
    } : {
      rootMargin,
      threshold
    };
    const observer = new IntersectionObserver(entries => {
      const [entry] = entries;
      if (entry.isIntersecting) {
        setInView(true);
        if (triggerOnce) {
          observer.disconnect();
        }
      } else if (!triggerOnce) {
        setInView(false);
      }
    }, mobileSettings);
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, triggerOnce]);
  return {
    ref,
    inView
  };
};